<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import EstimatedTime from '@/components/ui/EstimatedTime.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import Common from '@/libs/common';
import { AppRoute } from '@/libs/routing';
import { buildStopDepartures, type DepartureTime } from './stop-departures';
import type { StopTimeData } from '@/@types/gtfs-ressource';
import { useGtfsStore } from '@/stores/gtfs';

const props = defineProps<{
  stopId: string;
  /** This stop's stop_times for the day, sliced by the sheet from a single shared traversal. */
  stopTimes: StopTimeData[];
  /** Shared 5s-refreshed timestamp (seconds), owned by the sheet. */
  now: number;
}>();

const gtfsStore = useGtfsStore();
const router = useRouter();

const stopIds = computed<string[]>(() => gtfsStore.stations[props.stopId] || [props.stopId]);

const groups = computed(() =>
  buildStopDepartures(props.stopTimes, stopIds.value, gtfsStore.gtfsData.routes, props.now, 2),
);

function formatTime(departure: DepartureTime): string {
  const departTime = departure.timestamp - props.now;
  if (departTime < 3600) return Common.formatSec(departTime);
  return Common.timestampFormatHHMM(departure.timestamp);
}

function goToStop() {
  router.push({ name: AppRoute.STOP_DETAILED, params: { stopId: props.stopId } });
}
</script>

<template>
  <div class="stop-departure-cards">
    <!-- No trip serves this stop today (the stop view shows the same state) -->
    <div
      v-if="groups.length === 0"
      class="stop-departure-cards__card stop-departure-cards__empty"
      @click="goToStop"
    >
      <BaseIcon name="moon" class="stop-departure-cards__empty-icon" />
      {{ $t('common.noServiceToday') }}
    </div>
    <div v-for="group in groups" :key="group.routeId" class="stop-departure-cards__card" @click="goToStop">
      <div v-for="row in group.rows" :key="row.rowId" class="stop-departure-cards__row">
        <div class="stop-departure-cards__direction">
          <RouteBadge v-if="group.route" :route="group.route" size="small" />
          <div class="stop-departure-cards__headsign">
            <span class="stop-departure-cards__arrow"><BaseIcon name="arrow-right" /></span>
            <span class="stop-departure-cards__headsign-text">{{ row.tripHeadsign }}</span>
          </div>
        </div>
        <div class="stop-departure-cards__times">
          <EstimatedTime
            :time="row.times[0] ? formatTime(row.times[0]) : undefined"
            :type="row.times[0] ? (row.times[0].realTime ? 'realtime' : 'theoretical') : 'finished'"
          />
          <span v-if="row.times[1]" class="stop-departure-cards__next-time">
            {{ formatTime(row.times[1]) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.stop-departure-cards {
  display: flex;
  flex-direction: column;
  gap: $spacing-3;

  &__card {
    background-color: $color-white;
    border-radius: $radius-3xl;
    border: 1px solid $color-border-medium;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: $spacing-4;
    padding: $spacing-4;

    &:active {
      background-color: $color-neutral-lower;
    }
  }

  &__empty {
    align-items: center;
    color: $color-neutral-higher;
    flex-direction: row;
    font: $text-small;
    gap: $spacing-2;
    letter-spacing: $text-small-spacing;
  }

  &__empty-icon {
    color: $color-secondary;
    font-size: 16px;
  }

  &__row {
    display: flex;
    gap: $spacing-3;
    justify-content: space-between;
  }

  &__direction {
    align-items: flex-start;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-2;
    min-width: 0;
  }

  &__headsign {
    align-items: center;
    align-self: stretch;
    color: $color-text-dark;
    display: flex;
    font: $label-medium;
    gap: $spacing-2;
    letter-spacing: $label-medium-spacing;
    min-width: 0;
  }

  &__headsign-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__arrow {
    align-items: center;
    background-color: $color-neutral-lower;
    border-radius: $radius-tag;
    color: $color-text-neutral;
    display: flex;
    flex-shrink: 0;
    font-size: 16px;
    height: 24px;
    justify-content: center;
    width: 24px;
  }

  &__times {
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: $spacing-1;
  }

  &__next-time {
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
  }
}
</style>
